import type { ApiResponse, User, Client } from "@/types";
import type { IAuthService } from "../types";
import { TOKEN_KEY } from "@shared/constants/auth";
import { GoogleAuthProvider } from "firebase/auth";
import {
  firebaseSignIn,
  firebaseSignUp,
  firebaseSignOut,
  firebaseSignInWithGoogle,
  getFirebaseIdToken,
} from "@services/config/firebase";
import { legacyHttpClient } from "@services/http/legacyHttpClient";
import { profileService } from "./profile.service";

/**
 * Transform backend Client model to frontend User model.
 * Combines Client data (from legacy Java backend) with Firebase uid.
 * Handles null/undefined fields gracefully (e.g., address, phone default to null).
 * @param client - Backend Client object with fullname, surname, email, etc.
 * @param uid - Firebase unique identifier from authentication
 * @returns Frontend User object with unified fields for UI consumption
 */
function clientToUser(client: Client, uid: string): User {
  return {
    id: String(client.uuid ?? uid),
    name: [client.fullname, client.surname].filter(Boolean).join(" "),
    uid,
    mail: client.email,
    address: client.address ?? null,
    phone: client.phone ?? null,
    notifications: true,
    image: null,
    cedula: client.uuid ?? null, // ← correct field from Client model
  };
}

/** Production auth service — Firebase + legacy Java backend. */
export const realAuthService: IAuthService = {
  async signIn(email: string, password: string): Promise<ApiResponse<User>> {
    try {
      const { idToken, uid } = await firebaseSignIn(email, password);
      localStorage.setItem(TOKEN_KEY, idToken);

      // Get client profile from legacy backend
      const client = await legacyHttpClient.post<Client>(
        "/api/web/get_client_by_email",
        { email },
      );

      const user = clientToUser(client, uid);
      return { success: true, data: user };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (error.code === "auth/email-not-verified") {
        return {
          success: false,
          data: {} as User,
          message: "Debes verificar tu correo antes de iniciar sesión.",
        };
      }

      return {
        success: false,
        data: {} as User,
        message: error.message || "Error al iniciar sesión",
      };
    }
  },

  async signUp(
    email: string,
    password: string,
    profile: {
      fullName: string;
      cedula: string;
      phone: string;
      address: string;
    },
  ): Promise<ApiResponse<User>> {
    try {
      const { uid } = await firebaseSignUp(email, password, profile.fullName);

      // Save billing data to create the Client in the legacy backend
      const [fullname, ...surnameParts] = profile.fullName.trim().split(" ");
      const surname = surnameParts.join(" ") || fullname;

      await legacyHttpClient.post("/api/web/save_billing_data", {
        email,
        data: {
          uuid: profile.cedula,
          type: "CI",
          address: profile.address,
          phone: profile.phone,
          name: fullname,
          surname,
        },
      });

      const user: User = {
        id: profile.cedula,
        name: profile.fullName,
        uid,
        mail: email,
        address: profile.address,
        phone: profile.phone,
        notifications: true,
        image: null,
        cedula: profile.cedula,
      };

      return {
        success: true,
        data: user, // ← return the actual user object
        message:
          "Cuenta creada. Revisa tu correo para verificar antes de iniciar sesión.",
      };
    } catch (error) {
      return {
        success: false,
        data: {} as User,
        message:
          error instanceof Error ? error.message : "Error al crear la cuenta",
      };
    }
  },

  // AFTER
  async signInWithGoogle(): Promise<ApiResponse<User>> {
    try {
      const { idToken, uid, email, displayName } = await firebaseSignInWithGoogle();
      localStorage.setItem(TOKEN_KEY, idToken);

      // Build User directly from Firebase — same approach as the React Native app,
      // which never calls the backend after Google sign-in either.
      const user: User = {
        id: uid,
        uid,
        name: displayName ?? "",
        mail: email,
        address: null,
        phone: null,
        cedula: null,
        notifications: true,
        image: null,
      };

      return { success: true, data: user };
    } catch (error) {
      return {
        success: false,
        data: {} as User,
        message:
          error instanceof Error
            ? error.message
            : "Error al iniciar sesión con Google",
      };
    }
  },

  async signOut(): Promise<void> {
    try {
      await firebaseSignOut();
    } catch {
      // Ignore errors from Firebase sign out
    }
    this.clearToken();
  },

  async getCurrentUser(): Promise<ApiResponse<User>> {
    try {
      const token = await getFirebaseIdToken();
      if (!token) {
        return { success: false, data: {} as User, message: "No autenticado" };
      }
      localStorage.setItem(TOKEN_KEY, token);

      // The token is a JWT — decode the payload to get email and sign-in provider.
      // Firebase claims (email, name, picture, firebase.sign_in_provider) are
      // cryptographically verified by the Firebase SDK before getIdToken() returns.
      const segment = token.split(".")[1] ?? "";
      const payload = JSON.parse(atob(segment)) as {
        email?: string;
        user_id?: string;
        name?: string;
        picture?: string;
        firebase?: {
          sign_in_provider?: string;
        };
      };
      if (!payload.email) {
        return { success: false, data: {} as User, message: "Token inválido" };
      }

      // Google Sign-In users: consultar backend para obtener datos editados.
      // Si no existe en el backend, usar datos del JWT como fallback.
      if (payload.firebase?.sign_in_provider === GoogleAuthProvider.PROVIDER_ID) {
        try {
          const client = await legacyHttpClient.post<Client>(
            "/api/web/get_client_by_email",
            { email: payload.email },
          );
          // Cliente encontrado en backend — devolver datos reales (incluye ediciones)
          const googleUser = clientToUser(client, payload.user_id ?? "");
          // Mantener imagen de Google ya que el backend no la almacena
          return { success: true, data: { ...googleUser, image: payload.picture ?? null } };
        } catch {
          // Sin cuenta en backend aún — construir desde JWT como fallback
          const googleUser: User = {
            id: payload.user_id ?? "",
            name: payload.name ?? payload.email ?? "",
            uid: payload.user_id ?? "",
            mail: payload.email ?? "",
            address: null,
            phone: null,
            notifications: true,
            image: payload.picture ?? null,
            cedula: null,
          };
          return { success: true, data: googleUser };
        }
      }

      const client = await legacyHttpClient.post<Client>(
        "/api/web/get_client_by_email",
        { email: payload.email },
      );

      const user = clientToUser(client, payload.user_id ?? "");
      return { success: true, data: user };
    } catch (error) {
      return {
        success: false,
        data: {} as User,
        message:
          error instanceof Error ? error.message : "Error al obtener usuario",
      };
    }
  },

  getToken(): string | null {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },

  setToken(token: string): void {
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch {
      // Storage bloqueado (SecurityError) — el usuario tendrá que re-autenticarse
    }
  },

  clearToken(): void {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      // Storage bloqueado — ignorar
    }
  },

  isAuthenticated(): boolean {
    return !!this.getToken();
  },

  async updateProfile(
    email: string,
    fullname: string,
    address: string,
    phone: string,
    cedula?: string,
  ): Promise<ApiResponse<unknown>> {
    return profileService.updateProfile(email, fullname, address, phone, cedula);
  },
};
